import { css, type StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { TicketPriority, TicketStatus } from '../../../models/Support/SupportEnums';
import type { BulkTicketUpdate } from '../../../queries/Support/useSupportTicketMutations';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { Button } from '../../../web/components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { ticketTableStyles as ts } from './styles/ticketTableStyles';

export interface EditMenuOption<T extends string = string> {
	value: T;
	label: string;
}

export const STATUS_EDIT_OPTIONS: ReadonlyArray<EditMenuOption<TicketStatus>> = [
	{ value: TicketStatus.Open, label: 'Active' },
	{ value: TicketStatus.Closed, label: 'Closed' },
];

export const PRIORITY_EDIT_OPTIONS: ReadonlyArray<EditMenuOption<TicketPriority>> = [
	{ value: TicketPriority.Low, label: 'Low' },
	{ value: TicketPriority.Normal, label: 'Normal' },
	{ value: TicketPriority.High, label: 'High' },
	{ value: TicketPriority.Urgent, label: 'Urgent' },
];

interface TicketEditMenuProps<T extends string> {
	children: React.ReactNode;
	options: ReadonlyArray<EditMenuOption<T>>;
	onSelect: (value: T) => void;
	triggerStyles: StyleDeclarationValue[];
	title?: string;
	disabled?: boolean;
}

export function TicketEditMenu<T extends string>({
	children,
	options,
	onSelect,
	triggerStyles,
	title,
	disabled,
}: TicketEditMenuProps<T>) {
	return (
		<Dropdown>
			<DropdownTrigger
				disabled={disabled}
				render={<Button type='button' variant='ghost' styles={triggerStyles} title={title} />}
			>
				{children}
				<ChevronDown className={css(ts.cellEditChevron)} width={12} height={12} />
			</DropdownTrigger>
			<DropdownContent align='start' styles={[ts.filterMenu]}>
				{options.map(option => (
					<DropdownItem key={option.value} styles={[ts.filterMenuItem]} onClick={() => onSelect(option.value)}>
						{option.label}
					</DropdownItem>
				))}
			</DropdownContent>
		</Dropdown>
	);
}

interface TicketBulkActionsBarProps {
	selectedCount: number;
	isPending: boolean;
	assigneeOptions: ReadonlyArray<EditMenuOption<string>>;
	categoryOptions: ReadonlyArray<EditMenuOption<string>>;
	onApply: (update: BulkTicketUpdate) => void;
	onClear: () => void;
}

export const TicketBulkActionsBar: React.FC<TicketBulkActionsBarProps> = ({
	selectedCount,
	isPending,
	assigneeOptions,
	categoryOptions,
	onApply,
	onClear,
}) => (
	<div className={css(ts.selectionBar)}>
		<span className={css(ts.selectionCount)}>
			{isPending ? `Updating ${selectedCount}…` : `${selectedCount} selected`}
		</span>
		<TicketEditMenu
			options={assigneeOptions}
			onSelect={value => onApply({ field: 'assignee', assigneeId: value || null })}
			triggerStyles={[ts.bulkMenuBtn]}
			disabled={isPending}
		>
			Assign
		</TicketEditMenu>
		<TicketEditMenu
			options={STATUS_EDIT_OPTIONS}
			onSelect={status => onApply({ field: 'status', status })}
			triggerStyles={[ts.bulkMenuBtn]}
			disabled={isPending}
		>
			Status
		</TicketEditMenu>
		<TicketEditMenu
			options={PRIORITY_EDIT_OPTIONS}
			onSelect={priority => onApply({ field: 'priority', priority })}
			triggerStyles={[ts.bulkMenuBtn]}
			disabled={isPending}
		>
			Priority
		</TicketEditMenu>
		<TicketEditMenu
			options={categoryOptions}
			onSelect={value => onApply({ field: 'category', categoryId: value || null })}
			triggerStyles={[ts.bulkMenuBtn]}
			disabled={isPending}
		>
			Category
		</TicketEditMenu>
		<Button type='button' variant='ghost' styles={[ts.selectionClearBtn]} onClick={onClear} disabled={isPending}>
			Clear selection
		</Button>
	</div>
);
